import { useEffect, useRef, useState } from "react";
import { portalApi, INR, Page, StatCard, usePortal } from "./PortalShell";
import { TrendingUp, Users, Award, Gift, Sparkles, Target, CheckCircle2, Circle, Zap, ChevronRight, Clock, Flame, Trophy } from "lucide-react";
import EarningsCoachSection from "./EarningsCoachSection";
import WeeklyDigestSection from "./WeeklyDigestSection";

const TIER_GRADIENT = {
  Base: "from-neutral-500 to-neutral-700",
  Pro: "from-sky-500 to-blue-700",
  "Pro Advanced": "from-purple-500 to-indigo-700",
  Legend: "from-orange-500 to-red-600",
};

// Next commission-rate milestone (matches the ladder used on the CRM tier logic).
function nextCommissionMilestone(newThisMonth, currentPct) {
  const ladder = [
    { new: 2, pct: 12 },
    { new: 5, pct: 15 },
    { new: 10, pct: 18 },
    { new: 15, pct: 20 },
  ];
  for (const m of ladder) {
    if (newThisMonth < m.new && currentPct < m.pct) return { need: m.new - newThisMonth, targetPct: m.pct, atNew: m.new };
  }
  return null;
}

// Animated counter — rolls the value up from 0 whenever it changes. Pure CSS/JS, no deps.
function CountUp({ value, duration = 900, format = (v) => v, testId }) {
  const [n, setN] = useState(0);
  const raf = useRef();
  const prev = useRef(0);
  useEffect(() => {
    const start = prev.current;
    const end = Number(value) || 0;
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
      const cur = start + (end - start) * eased;
      setN(cur);
      if (p < 1) raf.current = requestAnimationFrame(step);
      else prev.current = end;
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [value, duration]);
  return <span data-testid={testId}>{format(n)}</span>;
}

export default function PortalDashboardPage() {
  const { profile } = usePortal();
  const [me, setMe] = useState(null);
  const [nextSteps, setNextSteps] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    portalApi.get("/affiliate/me").then((r) => setMe(r.data)).catch((e) => setErr(e?.response?.data?.detail || "Load failed"));
    portalApi.get("/affiliate/next-steps").then((r) => setNextSteps(r.data)).catch(() => {});
  }, []);

  if (err) return <Page title="Dashboard"><div className="text-red-400">{err}</div></Page>;
  if (!me) return <Page title="Dashboard"><div className="text-neutral-500 italic">Loading…</div></Page>;

  const { snapshot, rewards_ledger, referrals, milestones } = me;
  const grad = TIER_GRADIENT[profile.tier] || TIER_GRADIENT.Base;
  const monthName = new Date().toLocaleString("en-US", { month: "long" });

  return (
    <Page title={`Hey, ${profile.name.split(" ")[0]}`} subtitle="Two benefits: cash in your pocket + free product on us 💰" testId="portal-dashboard-page">
      {/* Tier + rate top strip (small — used to be huge; now understated) */}
      <div className="flex items-center gap-2 flex-wrap">
        <span data-testid="hero-tier-pill" className={`inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r ${grad} px-3 py-1 text-xs font-semibold uppercase tracking-widest text-white`}>
          <Trophy className="h-3 w-3" /> {profile.tier}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-900 border border-neutral-800 px-3 py-1 text-xs font-semibold">
          {snapshot.commission_percent}% commission
        </span>
        {profile.asm_name && (
          <span className="text-[11px] text-neutral-500">ASM: <b className="text-neutral-300">{profile.asm_name}</b></span>
        )}
      </div>

      {/* Two big benefit cards — Commission + Free Products */}
      <div data-testid="dashboard-hero" className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Card 1 — Commission (orange) */}
        {(() => {
          const commEarned = snapshot.commission_amount || 0;
          const nextComm = snapshot.next_commission_slab; // { customers_needed, target_pct } | null
          const potentialAtTarget = nextComm
            ? Math.round((snapshot.sales_total || 0) * nextComm.target_pct / 100)
            : 0;
          const bumpDelta = Math.max(0, potentialAtTarget - commEarned);
          return (
            <div
              data-testid="hero-commission-card"
              className="rounded-3xl bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 p-5 lg:p-6 relative overflow-hidden"
            >
              <Sparkles className="absolute top-4 right-4 h-4 w-4 text-white/30 animate-pulse" />
              <div className="text-[11px] uppercase tracking-widest text-white/80 flex items-center gap-1.5">
                <Flame className="h-3 w-3" /> {monthName} Commission Earned
              </div>
              <div className="mt-1 text-4xl sm:text-5xl font-black tracking-tight tabular-nums leading-none text-white">
                <CountUp
                  value={commEarned}
                  format={(v) => `₹${Math.round(v).toLocaleString("en-IN")}`}
                  testId="hero-earnings-value"
                />
              </div>
              <div className="mt-2 text-xs text-white/80">
                <b className="text-white">{profile.lifetime_new_customers}</b> lifetime new customers · <b className="text-white">₹{Number(profile.lifetime_sales || 0).toLocaleString("en-IN")}</b> in sales driven
              </div>
              {/* Suggestion — how to earn more commission */}
              <div className="mt-4 rounded-xl bg-black/25 border border-white/15 p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/70 mb-1">How to earn more commission</div>
                {nextComm ? (
                  <div className="text-xs text-white leading-relaxed">
                    <b>Bring in {nextComm.customers_needed} more new customer{nextComm.customers_needed === 1 ? "" : "s"}</b> this month → your commission percent jumps to <b>{nextComm.target_pct}%</b>
                    {bumpDelta > 0 && <> and you'd earn <b>₹{bumpDelta.toLocaleString("en-IN")} more</b>.</>}
                  </div>
                ) : (
                  <div className="text-xs text-white leading-relaxed">
                    You're at the top commission slab. Keep going to compound the earnings.
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Card 2 — Free Products (emerald) */}
        {(() => {
          const stackUnlocked = snapshot.stack_mrp_unlocked || 0;
          const nextStack = snapshot.next_stack_slab; // { sales_min, mrp, sales_gap } | null
          return (
            <div
              data-testid="hero-stack-card"
              className="rounded-3xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 p-5 lg:p-6 relative overflow-hidden"
            >
              <Gift className="absolute top-4 right-4 h-4 w-4 text-white/30 animate-pulse" />
              <div className="text-[11px] uppercase tracking-widest text-white/80 flex items-center gap-1.5">
                <Gift className="h-3 w-3" /> {monthName} Free Products Unlocked
              </div>
              <div className="mt-1 text-4xl sm:text-5xl font-black tracking-tight tabular-nums leading-none text-white">
                <CountUp
                  value={stackUnlocked}
                  format={(v) => `₹${Math.round(v).toLocaleString("en-IN")}`}
                  testId="hero-stack-value"
                />
              </div>
              <div className="mt-2 text-xs text-white/80">
                MRP of Fuelled stack shipped free to you this month.
              </div>
              {/* Suggestion — how to earn more free stack */}
              <div className="mt-4 rounded-xl bg-black/25 border border-white/15 p-3">
                <div className="text-[10px] uppercase tracking-widest text-white/70 mb-1">How to earn more free products</div>
                {nextStack ? (
                  <div className="text-xs text-white leading-relaxed">
                    <b>Drive ₹{Number(nextStack.sales_gap || 0).toLocaleString("en-IN")} more in sales</b> this month → unlock <b>₹{Number(nextStack.mrp || 0).toLocaleString("en-IN")}</b> of free Fuelled stack.
                  </div>
                ) : (
                  <div className="text-xs text-white leading-relaxed">
                    You've hit the top stack slab this month. Well done.
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Milestone chips — streak + peer rank */}
      {(milestones?.earning_streak_months >= 2 || milestones?.peer_top_pct != null) && (
        <div data-testid="milestone-chips" className="flex flex-wrap gap-2">
          {milestones?.earning_streak_months >= 2 && (
            <div
              data-testid="milestone-streak"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-orange-950/60 via-orange-900/40 to-amber-900/40 border border-orange-800/50 pl-2 pr-3 py-1.5 text-xs"
            >
              <span className="text-base leading-none">🔥</span>
              <span>
                <b className="text-orange-200">{milestones.earning_streak_months} months</b>
                <span className="text-neutral-400"> in a row earning</span>
              </span>
            </div>
          )}
          {milestones?.peer_top_pct != null && (
            <div
              data-testid="milestone-rank"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-sky-950/60 via-blue-950/50 to-indigo-950/40 border border-sky-800/50 pl-2 pr-3 py-1.5 text-xs"
            >
              <Trophy className="h-3.5 w-3.5 text-sky-300" />
              <span>
                <b className="text-sky-200">Top {milestones.peer_top_pct}%</b>
                <span className="text-neutral-400"> of {milestones.peer_tier}-tier affiliates this month</span>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Monthly snapshot */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={TrendingUp} label={`${monthName} Sales`} value={INR(snapshot.sales_total)} testId="stat-sales" />
        <StatCard icon={Users} label={`${monthName} New Customers`} value={snapshot.new_customers} testId="stat-new" />
        <StatCard icon={Award} label="Commission %" value={`${snapshot.commission_percent}%`} testId="stat-pct" />
        <StatCard icon={Gift} label={`${monthName} Earnings`} value={<CountUp value={snapshot.commission_amount} format={(v) => `₹${Math.round(v).toLocaleString("en-IN")}`} />} testId="stat-comm" highlight />
      </div>

      {/* Lifetime snapshot */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard icon={TrendingUp} label="Lifetime Sales" value={INR(profile.lifetime_sales || 0)} testId="stat-lifetime-sales" />
        <StatCard icon={Users} label="Lifetime New Customers" value={profile.lifetime_new_customers} testId="stat-lifetime-new" />
      </div>

      {/* Starter kit (read-only) */}
      {me.starter_kit && (
        <div data-testid="portal-starter-kit" className="rounded-2xl bg-neutral-900 border border-neutral-800 px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-1">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Starter Kit</div>
          <span data-testid="portal-starter-kit-status" className={`text-sm font-semibold ${me.starter_kit.purchased ? "text-emerald-400" : "text-neutral-400"}`}>
            {me.starter_kit.purchased ? "Purchased" : "Not purchased yet"}
          </span>
          {me.starter_kit.purchased && (
            <span className="text-xs text-neutral-400">
              {me.starter_kit.purchased_on && new Date(me.starter_kit.purchased_on + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              {me.starter_kit.invoice_number && <> · Invoice <b className="text-neutral-200">{me.starter_kit.invoice_number}</b></>}
              {me.starter_kit.invoice_amount != null && <> · {INR(me.starter_kit.invoice_amount)}</>}
            </span>
          )}
        </div>
      )}

      {/* Next Steps */}
      {nextSteps && <NextStepsBlock ns={nextSteps} />}

      {/* Rewards ledger */}
      {rewards_ledger.length > 0 && (
        <div data-testid="section-rewards">
          <div className="text-xs font-semibold uppercase tracking-widest text-neutral-500 mb-3">Rewards Earned</div>
          <div className="space-y-2">
            {rewards_ledger.map((r) => (
              <div key={r.type} className="flex items-center justify-between rounded-2xl bg-neutral-900 border border-neutral-800 px-4 py-3">
                <div>
                  <div className="text-sm font-medium">{r.label}</div>
                  {r.achieved_at && <div className="text-[10px] text-neutral-500 mt-0.5">Earned {new Date(r.achieved_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>}
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-orange-400">₹{r.mrp.toLocaleString("en-IN")}</div>
                  <div className={`text-[10px] uppercase tracking-widest ${r.fulfilled ? "text-emerald-400" : "text-amber-400"}`}>
                    {r.fulfilled ? "Paid" : "Pending"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* This Week's Playbook — Monday WhatsApp digest preview */}
      <WeeklyDigestSection />

      {/* AI Earnings Coach — tailored growth plan */}
      <EarningsCoachSection />

      {/* Quick referral status */}
      {referrals.counts.pending + referrals.counts.queued + referrals.counts.paid > 0 && (
        <div className="rounded-2xl bg-neutral-900 border border-neutral-800 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">Your Referrals ({referrals.year})</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">
              {referrals.slots_left} / {referrals.program.annual_cap} slots left
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-3 text-center">
            <MiniStat label="Paid" value={INR(referrals.mrp.paid)} cls="text-emerald-400" />
            <MiniStat label="Queued" value={INR(referrals.mrp.queued)} cls="text-sky-400" />
            <MiniStat label="Pending" value={INR(referrals.mrp.pending)} cls="text-amber-400" />
          </div>
        </div>
      )}
    </Page>
  );
}

function MiniStat({ label, value, cls }) {
  return (
    <div className="rounded-xl bg-neutral-950 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`text-sm font-semibold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

function NextStepsBlock({ ns }) {
  const items = [];
  const daysLeftInMonth = () => {
    const now = new Date();
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return Math.max(0, Math.ceil((end - now) / 86400000));
  };

  // Fast Track
  if (ns.fast_track && ns.fast_track.status) {
    const ft = ns.fast_track;
    const pct = Math.min(100, Math.round((ft.new_customers_so_far / ft.target_new_customers) * 100));
    const done = ft.status === "achieved";
    const missed = ft.status === "missed";
    items.push({
      key: "fast_track",
      icon: Zap,
      title: "Fast Track Bonus",
      subtitle: `${ft.new_customers_so_far} of ${ft.target_new_customers} new customers · reward ₹${ft.reward_mrp.toLocaleString("en-IN")}`,
      explain: done
        ? "You did it! The ₹2,000 MRP bonus is yours."
        : missed
        ? "The 30-day window closed. This bonus is not available anymore."
        : `Get ${ft.target_new_customers - ft.new_customers_so_far} more people to buy using your code in the next ${ft.days_remaining} days to earn a free ₹2,000 product.`,
      progress: pct,
      done,
      barColor: done ? "bg-emerald-500" : missed ? "bg-rose-500" : "bg-orange-500",
      note: done ? "Achieved" : missed ? "Window closed" : `${ft.days_remaining} days left`,
      urgent: !done && !missed && ft.days_remaining <= 7,
    });
  }

  // Commission slab — clearest progress signal
  if (ns.commission_this_month && ns.commission_this_month.next_step) {
    const cts = ns.commission_this_month;
    const cur = cts.new_customers_this_month || 0;
    const need = cts.next_step.new_customers_needed || 0;
    const total = cur + need;
    const pct = total > 0 ? Math.min(100, Math.round((cur / total) * 100)) : 0;
    const dLeft = daysLeftInMonth();
    items.push({
      key: "commission",
      icon: Award,
      title: `Unlock ${cts.next_step.next_percent}% commission`,
      subtitle: `You're at ${cur} new customer${cur === 1 ? "" : "s"} this month · ${need} more to jump from ${cts.current_percent}% to ${cts.next_step.next_percent}%`,
      explain: `Every new person who buys with your code this month counts. Bring in ${need} more and your rate on every sale goes up.`,
      progress: pct,
      done: false,
      barColor: "bg-orange-500",
      note: `${dLeft} days left`,
      urgent: dLeft <= 7,
    });
  }

  // Tier progression steps — parse "X new customers" from progress string
  (ns.steps || []).forEach((s) => {
    const done = !!s.done;
    // Try to extract "X new customers → Y%" progression
    const progMatch = (s.progress || "").match(/(\d+)\s*new/i);
    const remainingMatch = (s.remaining || "").match(/(\d+)\s+more/i);
    const cur = progMatch ? parseInt(progMatch[1], 10) : 0;
    const need = remainingMatch ? parseInt(remainingMatch[1], 10) : 0;
    const total = cur + need;
    const pct = done ? 100 : total > 0 ? Math.min(100, Math.round((cur / total) * 100)) : 0;
    const dLeft = daysLeftInMonth();
    items.push({
      key: s.target,
      icon: Target,
      title: s.target,
      subtitle: s.progress || s.requirement,
      explain: done ? "You're already there. Great work." : (s.remaining || "Keep bringing in new buyers via your code."),
      progress: pct,
      done,
      barColor: done ? "bg-emerald-500" : "bg-orange-500",
      note: done ? "Done" : `${dLeft} days left`,
      urgent: !done && dLeft <= 7,
    });
  });

  if (items.length === 0) return null;

  return (
    <div data-testid="section-next-steps">
      <div className="mb-4">
        <div className="text-xs font-semibold uppercase tracking-widest text-orange-400 flex items-center gap-1.5">
          <Zap className="h-3 w-3" /> Ways To Earn More
        </div>
        <div className="text-[11px] text-neutral-500 mt-1">Small moves. Bigger cheques.</div>
      </div>
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.key} data-testid={`next-step-${it.key}`} className={`rounded-2xl border px-4 py-4 ${it.done ? "border-emerald-800/50 bg-emerald-950/20" : it.urgent ? "border-orange-700/50 bg-orange-950/20" : "border-neutral-800 bg-neutral-900"}`}>
            <div className="flex items-start gap-3">
              <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${it.done ? "bg-emerald-500/10 text-emerald-400" : "bg-neutral-800 text-orange-400"}`}>
                {it.done ? <CheckCircle2 className="h-4 w-4" /> : <it.icon className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm font-semibold">{it.title}</div>
                  <div className={`text-[10px] uppercase tracking-widest shrink-0 flex items-center gap-1 ${it.done ? "text-emerald-400" : it.urgent ? "text-orange-400 animate-pulse" : "text-neutral-500"}`}>
                    {it.urgent && !it.done && <Clock className="h-3 w-3" />}
                    {it.note}
                  </div>
                </div>
                {it.subtitle && <div className="text-xs text-neutral-400 mt-1">{it.subtitle}</div>}
                {it.explain && <div className="text-xs text-neutral-300 mt-2 leading-relaxed">{it.explain}</div>}
                <div className="h-2 rounded-full bg-neutral-800 overflow-hidden mt-3">
                  <div className={`h-full rounded-full transition-all ${it.barColor}`} style={{ width: `${it.progress}%` }} />
                </div>
                <div className="text-[10px] text-neutral-500 mt-1 font-mono">{it.progress}% complete</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
